import type { DraggableAttributes, DraggableSyntheticListeners } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { ReactNode } from 'react';

export interface SortableCriteriaItemDragHandleProps {
  attributes: DraggableAttributes;
  listeners: DraggableSyntheticListeners;
}

export interface SortableCriteriaItemProps {
  /** Stable id — must match one of the ids passed to the enclosing `SortableContext`. */
  id: string;
  /** Render prop so the caller can forward `dragHandleProps` into its own card component. */
  children: (dragHandleProps: SortableCriteriaItemDragHandleProps) => ReactNode;
}

/**
 * Wraps one draggable card in a CSS-grid list of criteria/variable cards.
 * Renders as a plain `div` grid item so the existing `auto-fill` grid layout
 * (cohort criteria, analytical dataset variables) reflows unchanged; only
 * the transform/transition needed for drag animation is applied here.
 */
export const SortableCriteriaItem = ({ id, children }: SortableCriteriaItemProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });

  return (
    <div
      ref={setNodeRef}
      style={{
        // Flex so the card fills the grid row's height and sortable cards match
        // the height of their non-sortable neighbours.
        display: 'flex',
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 1 : undefined,
      }}
    >
      {children({ attributes, listeners })}
    </div>
  );
};
